import React, { Fragment, useState, useMemo } from 'react';
import { Button, ButtonGroup, ListGroup, ListGroupItem, Progress } from 'reactstrap'
import { useTranslation, Trans } from 'react-i18next';
import { toast } from 'components/Shared/Toast';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import clsx from 'clsx';
import produce from 'immer'

import bootbox from 'components/Modals/Bootbox'
import { fetchContractCollaborators } from 'api/actions/collaboration';
import { contractAction } from 'api/actions/contracts'
import { fetcher } from 'api/client';
import { useProfileState, ProfileCan } from 'context/profile'
import useInterval from 'hooks/useInterval'
import { formatDistanceToNow, noop, } from 'utils'
import Whoami from 'actions/whoami-react'

import { TenantMembersSelect } from '../Collaborators/TenantMembersSelect'
import { TimelineEventsList, FetchingProgressLoader } from '../Collaborators/Collaborators'
import { undefinedEndDate } from 'app-constants';

function Voting({ contract }) {
    const { profile } = useProfileState()
    const { voteStarted } = contract;
    const votersQueryId = ['contract', contract._id, 'voters'];
    const [isFetching, setIsFetching] = useState(false)
    const whoami = new Whoami(contract, profile.email, profile._id, null, profile.organization);
    const voteHasEnded = whoami.voteHasEnded();

    const handleFetchingChange = (isFetching) => {
        setIsFetching(isFetching);
    }

    return (
        <div className="contract-collaborators active">

            <div className="detail mb-3 px-3 pt-3">
                {!voteStarted &&
                    <p>
                        <Trans
                            i18nKey={'voting.instructions'}
                            components={{ b: <strong /> }}
                        />
                    </p>
                }
                {voteStarted ?
                    <VotingStatus contract={contract} />
                    :
                    <ProfileCan I="modify" this={contract} field="voters">
                        <VotingSelect
                            contract={contract}
                            votersQueryId={votersQueryId}
                            onFetchingChange={handleFetchingChange}
                        />
                    </ProfileCan>
                }
            </div>
            <VotingToolbar
                contract={contract}
                voteHasEnded={voteHasEnded}
                votersQueryId={votersQueryId}
                onFetchingChange={handleFetchingChange}
            />
            { isFetching && <FetchingProgressLoader />}
            <VoterList
                contract={contract}
                votersQueryId={votersQueryId}
                onFetchingChange={handleFetchingChange}
            />
        </div>
    )
};

export default Voting;

function VotingToolbar({ contract, isOwner, voteHasEnded, votersQueryId, onFetchingChange }) {
    const { voteStarted, voters } = contract;
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const { mutate: mutateContractAction } = useMutation(({ contractId, action, ...payload }) => fetcher(contractAction(contractId, action, payload)), {
        onMutate: () => {
            onFetchingChange(true)
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(votersQueryId)
            queryClient.setQueryData(['contract', contract._id], data.contract)
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
        onSettled: () => {
            onFetchingChange(false)
        }
    })


    const handleStartVoting = async () => {
        const minutes = await bootbox.prompt({
            inputType: 'number',
            textT: t('voting.confirm_send')
        });
        if (minutes === null) return;

        mutateContractAction({
            contractId: contract._id,
            action: 'startvoting',
            minutes
        }, {
            onSuccess: (data) => {
                toast.success(t('voting.success'))
            }
        })
    };

    const handleEndVoting = async () => {
        const resp = await bootbox.confirm(t('voting.cancel_confirm'));
        if (!resp) return;

        mutateContractAction({
            contractId: contract._id,
            action: 'cancelvoting'
        }, {
            onSuccess: (data) => {
                toast.success(t('voting.cancel_success'))
            }
        })
    };


    return (
        <ButtonGroup className="d-flex align-items-center bg-grey">
            <VotingToolbarItem
                icon="thumbs-up"
                contract={contract}
                isDisabled={voteStarted || !voters?.length}
                onClick={handleStartVoting}
            >
                {t('voting.send')}
            </VotingToolbarItem>
            <VotingToolbarItem
                icon="ban"
                contract={contract}
                isDisabled={voteHasEnded || !voteStarted}
                onClick={handleEndVoting}
            >
                {t('voting.cancel')}
            </VotingToolbarItem>
        </ButtonGroup>
    )
}

function VotingToolbarItem({ className, contract, icon, isDisabled, onClick, children }) {
    return (
        <ProfileCan I="modify" this={contract} passThrough>
            {allowed =>
                <Button
                    color="light"
                    onClick={onClick}
                    className="border p-3 rounded-0"
                    disabled={isDisabled || !allowed}
                >
                    <span>{children}</span>
                    <i className={`ml-1 fas fa-${icon}`}></i>
                </Button>
            }
        </ProfileCan>

    )
}

export function VotingSelect({ contract, votersQueryId, onFetchingChange = noop }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { profile } = useProfileState()
    const { mutate: mutateContractAction, isLoading } = useMutation(({ contractId, action, ...payload }) => fetcher(contractAction(contractId, action, payload)), {
        onMutate: () => {
            onFetchingChange(true)
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(votersQueryId)
            queryClient.invalidateQueries(['contract', variables.contractId])
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
        onSettled: () => {
            onFetchingChange(false)
        }
    })

    const handleVoterChange = async (option, { action }) => {
        if (action === 'select-option') {
            const isVoter = contract.voters.find(voter => voter._id === option.value);
            const isMe = option.value === profile._id;

            if (isVoter) {
                return toast.error(t('contracts.collaborators.collaborator_exists'));
            } else if (isMe) {
                return toast.error(t('contracts.collaborators.collaborator_self'));
            } else {
                mutateContractAction({
                    contractId: contract._id,
                    action: 'addvoter',
                    collaboratorId: option.value
                }, {
                    onSuccess: (data) => {
                        queryClient.setQueryData(votersQueryId, produce(draft => {
                            draft.push(data.collaborator)
                        }))
                    }
                })
            }
        }
    }


    return (
        <TenantMembersSelect
            // className="mx-3"
            contractId={contract._id}
            isDisabled={isLoading}
            onChange={handleVoterChange}
        />
    )
}


const round = ((value, decimals) => Number(`${Math.round(`${value}e${decimals}`)}e-${decimals}`));

function VotingStatus({ contract }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const votingEndDate = contract.votingEndDate;
    const votingResults = useMemo(() => {
        const votes = contract.votes.reduce((results, vote) => {
            results[vote.status]++
            return results;
        }, { against: 0, for: 0, blank: 0, abstention: 0 });
        const total = contract.votes?.length ?? 1;

        return {
            against: votes.against,
            abstention: votes.abstention,
            forr: votes.for,
            blank: votes.blank,
            againstPercentage: round((votes.against / total) * 100, 2),
            forrPercentage: round((votes.for / total) * 100, 2),
            blankPercentage: round((votes.blank / total) * 100, 2),
            abstentionPercentage: round((votes.abstention / total) * 100, 2),
        };
    }, [contract.votes]);
    const [status, setStatus] = useState('calculating')
    const [counter, setCounter] = useState('')

    const countDownDate = new Date(votingEndDate).getTime();

    useInterval(() => {
        const now = new Date().getTime();
        setStatus('count-down')
        const distance = countDownDate - now;

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        setCounter(`${days}d ${hours}h ${minutes}m ${seconds}s `);

        if (distance < 0) {
            setStatus('voting-ended');
            queryClient.invalidateQueries(['contract', contract._id])
        }
    }, 1000);

    return (
        <div>
            {votingEndDate !== undefinedEndDate &&
                <VotingStatusProperty
                    icon="clock"
                    iconTooltip={t('voting.remaining_time')}
                >
                    <span>
                        {t('voting.remaining_time')}: {
                            status === 'voting-ended' ? <b>{t('voting.ended')}</b>
                                : status === 'calculating' ? <span>{t('voting.calculating')}...</span>
                                    : <span>{counter}</span>
                        }
                    </span>
                </VotingStatusProperty>
            }
            <VotingStatusProperty
                icon="vote-yea"
                iconClassName="text-dark-grey"
                iconTooltip={t('voting.requested')}
                label={t('voting.blank')}
                count={votingResults.blank}
                percentage={votingResults.blankPercentage}
            />
            <VotingStatusProperty
                icon="thumbs-up"
                iconClassName="text-success"
                iconTooltip={t('editor.collaboration.approved')}
                label={t('voting.for')}
                count={votingResults.forr}
                percentage={votingResults.forrPercentage}
            />
            <VotingStatusProperty
                icon="thumbs-down"
                iconClassName="text-danger"
                iconTooltip={t('editor.collaboration.rejected')}
                label={t('voting.against')}
                count={votingResults.against}
                percentage={votingResults.againstPercentage}
            />
            <VotingStatusProperty
                icon="hand-paper"
                iconClassName="text-blue"
                iconTooltip={t('voting.requested')}
                label={t('voting.abstention')}
                count={votingResults.abstention}
                percentage={votingResults.abstentionPercentage}
            />
            <div className="detail">
                <Progress multi style={{ height: '1rem' }}>
                    <Progress bar style={{ color: 'black' }} color="light" value={votingResults.blankPercentage}>{votingResults.blankPercentage}%</Progress>
                    <Progress bar color="success" value={votingResults.forrPercentage}>{votingResults.forrPercentage}%</Progress>
                    <Progress bar color="danger" value={votingResults.againstPercentage}>{votingResults.againstPercentage}%</Progress>
                    <Progress bar color="info" value={votingResults.abstentionPercentage}>{votingResults.abstentionPercentage}%</Progress>
                </Progress>
            </div>
        </div>
    )
}

function VotingStatusProperty({ className, icon, iconClassName, iconTooltip, label, count, percentage, children }) {
    return (
        <p className={className}>
            <i
                className={clsx(iconClassName, `fas fa-${icon}`)}
                title={iconTooltip}
                data-html="true"
            />{' '}

            {children ? children :
                <span>
                    {label}: <b>{count}</b> - ({percentage}%)
                </span>
            }
        </p>
    )
}


function VoterList({ contract, votersQueryId, onFetchingChange = noop }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data, status } = useQuery(votersQueryId, ({ queryKey: [, contractId] }) => fetcher(fetchContractCollaborators({ contractId, type: 'voters' })), {
        enabled: !!contract._id
    })
    const { mutate: mutateRemoveVoter } = useMutation(({ contractId, ...payload }) => fetcher(contractAction(contractId, 'removevoter', payload)), {
        onMutate: () => {
            onFetchingChange(true)
        },
        onSuccess: (data, variables) => {
            // TODO: handle better vobo elimination maybe use onMutate for better ux
            queryClient.setQueryData(['contract', contract._id, 'voters'], produce(draft => {
                const collaboratorId = variables.collaboratorId
                return draft.filter(voter => voter._id === collaboratorId)
            }))
            queryClient.invalidateQueries(['contract', variables.contractId])
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        },
        onSettled: () => {
            onFetchingChange(false)
        }
    })

    const handleRemoveVoter = async (voter) => {
        if (!contract.voteStarted) {
            const payload = {
                contractId: contract._id,
                collaboratorId: voter._id
            }


            await mutateRemoveVoter(payload)
        }
    }


    return (
        <ListGroup style={{ overFlow: 'auto' }}>
            {
                data?.map((voter, i) => {
                    const isLast = data?.length - 1 === i

                    return <VoterItem
                        key={voter._id}
                        className={clsx(isLast && 'mb-5')}
                        contract={contract}
                        voter={voter}
                        onRemove={handleRemoveVoter}
                    />
                })
            }
        </ListGroup>
    )
}

function VoterItem({ contract, voter, onRemove }) {
    const { t } = useTranslation();
    const { givenName, familyName, lastStatus, events, vote } = voter;
    const [isOpen, setIsOpen] = useState(false);

    const voteState = useMemo(() => {
        const voteMapper = {
            'blank': {
                icon: 'vote-yea',
                color: 'dark-grey',
                tooltip: t('voting.vote_blank'),
            },
            'for': {
                icon: 'thumbs-up',
                color: 'success',
                tooltip: t('voting.vote_for')
            },
            'against': {
                icon: 'thumbs-down',
                color: 'danger',
                tooltip: t('voting.vote_against')
            },
            'abstention': {
                icon: 'hand-paper',
                color: 'blue',
                tooltip: t('voting.vote_abstein'),
            }
        }

        return voteMapper[vote?.status ?? '']
    }, [vote, t])

    const toggle = () => setIsOpen(isOpen => !isOpen)

    const handleRemoveVoter = async () => {
        onRemove(voter)
    }

    return (
        <ListGroupItem>
            <div className="d-flex" >
                {!contract.voteStarted &&
                    <ProfileCan I="modify" this={contract}>
                        <div className="mr-2">
                            <Button color="default" onClick={handleRemoveVoter} >
                                <i className="fas fa-times"></i>
                            </Button>
                        </div>
                    </ProfileCan>
                }
                <div className="mr-2 align-self-center">
                    {!voteState && <i className="fas fa-user text-muted"></i>}
                </div>
                <div className="mr-2 align-self-center">
                    {voteState &&
                        <i
                            className={`mr-2 fas fa-${voteState.icon} text-${voteState.color}`}
                            title={voteState.tooltip}
                            data-html="true"
                            style={voteState.style}
                        />
                    }
                    <span className="text-muted">
                        {givenName} {familyName}{' '}
                    </span>
                    <span className="ml-2">
                        {lastStatus &&
                            <Fragment>{t(`events.${lastStatus.type}`)} {formatDistanceToNow(lastStatus.eventDate)}</Fragment>
                        }
                    </span>
                </div>
                <div className="ml-auto align-self-center">
                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} onClick={toggle}></i>
                </div>
            </div>
            <TimelineEventsList
                events={events}
                isOpen={isOpen}
            />
        </ListGroupItem>
    )
}

